import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import Card from '@/components/Card';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import {
  ShiftEventEntry,
  ShiftEventFilterOption,
  fetchShiftEventFilters,
  fetchShiftEvents,
} from '@/services/shiftEventsApi';

const PER_PAGE = 50;

const formatDateTime = (value: string | null) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatActor = (entry: ShiftEventEntry) => {
  const { actor } = entry;
  // System events with no human actor show only the timestamp.
  if (!actor || actor.type === 'system') return null;
  const label = actor.type === 'ops' ? 'Ops' : 'Worker';
  return `${label}: ${actor.name || `#${actor.id ?? '?'}`}`;
};

const ActionsLog = () => {
  const [rows, setRows] = useState<ShiftEventEntry[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [clientId, setClientId] = useState('');
  const [date, setDate] = useState('');

  // Dropdown options (only users/clients present in the feed)
  const [users, setUsers] = useState<ShiftEventFilterOption[]>([]);
  const [clients, setClients] = useState<ShiftEventFilterOption[]>([]);

  useEffect(() => {
    fetchShiftEventFilters()
      .then((res) => {
        setUsers(res.data.users);
        setClients(res.data.clients);
      })
      .catch(() => {
        // Non-fatal: the feed still works without dropdown options.
      });
  }, []);

  // Single debounced fetch keyed on all filters + page. Filter changes reset to
  // page 1 (handlers below), so page === 1 means "replace", otherwise "append".
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setFetching(true);

    const timer = setTimeout(
      () => {
        fetchShiftEvents({
          page,
          per_page: PER_PAGE,
          search: search || undefined,
          user_id: userId || undefined,
          client_id: clientId || undefined,
          date: date || undefined,
        })
          .then((res) => {
            if (cancelled) return;
            setRows((prev) => (page === 1 ? res.data : [...prev, ...res.data]));
            setTotalPages(res.pagination.total_pages);
          })
          .catch((e: unknown) => {
            if (cancelled) return;
            setError(
              e instanceof Error ? e.message : 'Failed to load actions log',
            );
          })
          .finally(() => {
            if (!cancelled) setFetching(false);
          });
      },
      search ? 300 : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, userId, clientId, date, page]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const onUser = (e: SelectChangeEvent<string>) => {
    setUserId(e.target.value);
    setPage(1);
  };
  const onClient = (e: SelectChangeEvent<string>) => {
    setClientId(e.target.value);
    setPage(1);
  };
  const onDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
    setPage(1);
  };
  const clearAll = () => {
    setSearch('');
    setUserId('');
    setClientId('');
    setDate('');
    setPage(1);
  };

  const hasMore = page < totalPages;

  return (
    <Page size="full" title="Actions Log (internal)">
      <Card>
        <Card.Section>
          <Stack gap={16} style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Stack vertical gap={4}>
              <Small color="lighter">Search</Small>
              <TextField
                size="small"
                value={search}
                onChange={onSearch}
                placeholder="event type, shift id, worker id"
                style={{ minWidth: 240 }}
              />
            </Stack>
            <Stack vertical gap={4}>
              <Small color="lighter">User</Small>
              <Select
                size="small"
                value={userId}
                onChange={onUser}
                displayEmpty
                style={{ minWidth: 180 }}
              >
                <MenuItem value="">All users</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack vertical gap={4}>
              <Small color="lighter">Client</Small>
              <Select
                size="small"
                value={clientId}
                onChange={onClient}
                displayEmpty
                style={{ minWidth: 180 }}
              >
                <MenuItem value="">All clients</MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack vertical gap={4}>
              <Small color="lighter">Date</Small>
              <TextField
                size="small"
                type="date"
                value={date}
                onChange={onDate}
              />
            </Stack>
            <Button variant="outlined" size="small" onClick={clearAll}>
              Clear filters
            </Button>
          </Stack>
        </Card.Section>

        {error ? (
          <Card.Section>
            <Body color="lighter">{error}</Body>
          </Card.Section>
        ) : (
          <Card.Section>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                }}
              >
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6B7280' }}>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                      Event type
                    </th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                      Shift
                    </th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                      Description
                    </th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                      Triggered by
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((entry) => {
                    const actorLabel = formatActor(entry);
                    return (
                      <tr
                        key={entry.id}
                        style={{ borderTop: '1px solid #E5E7EB' }}
                      >
                        <td style={{ padding: '8px 12px' }}>
                          <Body weight="semibold">{entry.event_type}</Body>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {entry.shift ? (
                            <Stack vertical gap={2}>
                              <Body>#{entry.shift.id}</Body>
                              {entry.shift.client_name && (
                                <Small color="lighter">
                                  {entry.shift.client_name}
                                </Small>
                              )}
                            </Stack>
                          ) : (
                            <Small color="lighter">—</Small>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <Body>{entry.description}</Body>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <Stack vertical gap={2}>
                            {actorLabel && <Body>{actorLabel}</Body>}
                            <Small color="lighter">
                              {formatDateTime(entry.created_at)}
                            </Small>
                          </Stack>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {rows.length === 0 && !fetching && (
                <Stack style={{ justifyContent: 'center', padding: '40px' }}>
                  <Body color="lighter">No events found.</Body>
                </Stack>
              )}

              {fetching && (
                <Stack style={{ justifyContent: 'center', padding: '24px' }}>
                  <CircularProgress size={24} />
                </Stack>
              )}
            </div>
          </Card.Section>
        )}

        {hasMore && !fetching && (
          <Card.Section>
            <Stack style={{ justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => setPage((p) => p + 1)}>
                Load more
              </Button>
            </Stack>
          </Card.Section>
        )}
      </Card>
    </Page>
  );
};

export default ActionsLog;
